import { Box, Switch } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import AchievementStatusBadge from 'src/modules/trails/components/achievement-status-badge';
import { CategoryStatus } from 'src/types/products';

export type ThemesCategoriesRow = {
  id: string;
  entityId: string;
  name: string;
  status: 'active' | 'inactive';
  statusLabel: string;
};

type GetThemesCategoriesColumnsParams = {
  currentTab: 'themes' | 'categories';
  canUpdateStatus: boolean;
  handleChangeStatus: (entityId: string, status: CategoryStatus) => void;
};

export const getThemesCategoriesColumns = ({
  currentTab,
  canUpdateStatus,
  handleChangeStatus,
}: GetThemesCategoriesColumnsParams): GridColDef[] => [
  {
    field: 'name',
    headerName: 'Nome',
    flex: 1.4,
    minWidth: 280,
    align: 'left',
    headerAlign: 'left',
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.7,
    minWidth: 160,
    align: 'left',
    headerAlign: 'left',
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams) => {
      const status = String(params.value || '') as CategoryStatus;
      const entityId = String(params.row.entityId || params.id || '');

      if (status === 'active' || status === 'inactive') {
        return (
          <Switch
            checked={status === 'active'}
            disabled={!canUpdateStatus || !entityId}
            inputProps={{
              'aria-label': `Alterar status ${
                currentTab === 'themes' ? 'do tema' : 'da categoria'
              } ${params.row.name}`,
            }}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              handleChangeStatus(entityId, event.target.checked ? 'active' : 'inactive');
            }}
            sx={{
              width: 65,
              height: 25,
              p: 0,
              overflow: 'visible',

              '& .MuiSwitch-switchBase': {
                p: '4px',
                transitionDuration: '220ms',

                '&.Mui-checked': {
                  transform: 'translateX(35px)',
                  color: 'common.white',

                  '& + .MuiSwitch-track': {
                    borderColor: 'success.main',
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    boxShadow: (theme) =>
                      `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.18)}, 0 8px 18px ${alpha(theme.palette.success.main, 0.24)}`,
                  },

                  '& + .MuiSwitch-track::before': {
                    opacity: 1,
                  },

                  '& + .MuiSwitch-track::after': {
                    opacity: 0,
                  },
                },

                '&.Mui-focusVisible .MuiSwitch-thumb': {
                  outline: (theme) => `3px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                  outlineOffset: 2,
                },
              },

              '& .MuiSwitch-thumb': {
                width: 17,
                height: 17,
                bgcolor: 'common.white',
                boxShadow: (theme) =>
                  `0 3px 10px ${alpha(theme.palette.grey[900], 0.22)}, inset 0 0 0 1px ${alpha(theme.palette.grey[500], 0.16)}`,
              },

              '& .MuiSwitch-track': {
                position: 'relative',
                borderRadius: 999,
                border: '1px solid',
                borderColor: 'error.main',
                opacity: 1,
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.darker} 100%)`,
                boxShadow: (theme) =>
                  `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.16)}, 0 8px 18px ${alpha(theme.palette.error.main, 0.2)}`,

                '&::before': {
                  content: '"Ativo"',
                  position: 'absolute',
                  left: 9,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#fff',
                  fontSize: 9,
                  lineHeight: 1,
                  fontWeight: 700,
                  opacity: 0,
                  transition: 'opacity 160ms ease',
                },

                '&::after': {
                  content: '"Inativo"',
                  position: 'absolute',
                  right: 7,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#fff',
                  fontSize: 9,
                  lineHeight: 1,
                  fontWeight: 700,
                  opacity: 1,
                  transition: 'opacity 160ms ease',
                },
              },
            }}
          />
        );
      }

      return (
        <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <AchievementStatusBadge
            status={String(params.value || '')}
            label={params.row.statusLabel}
          />
        </Box>
      );
    },
  },
];
